/**
 * Collapse slug vs display-name brand labels ("omsystem" / "om-system" /
 * "OM System") without merging distinct brands (Olympus ≠ OM System,
 * Panasonic ≠ Lumix).
 */

export function brandIdentityKey(name: string): string {
  return String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
}

const BRAND_FAMILIES: string[][] = [];

const FAMILY_BY_MEMBER = new Map<string, string[]>();
const FAMILY_DISPLAY = new Map<string, string>();
for (const family of BRAND_FAMILIES) {
  const display = family[0] === 'panasonic' ? 'Panasonic' : family[0];
  for (const member of family) {
    FAMILY_BY_MEMBER.set(member, family);
    FAMILY_DISPLAY.set(member, display);
  }
}

export function brandFamilyKey(name: string): string {
  const key = brandIdentityKey(name);
  const family = FAMILY_BY_MEMBER.get(key);
  return family ? family[0] : key;
}

export function brandFamilyDisplayName(name: string): string | undefined {
  return FAMILY_DISPLAY.get(brandIdentityKey(name));
}

/** Prefer "OM System" over "omsystem" or "OM SYSTEM". */
export function preferBrandDisplayName(current: string, next: string): string {
  const score = (value: string): number => {
    const letters = value.replace(/[^A-Za-z]/g, '');
    const spaced = /\s/.test(value) ? 4 : 0;
    const slug = /^[a-z0-9_-]+$/.test(value) ? -3 : 0;
    const allCaps = letters.length > 3 && letters === letters.toUpperCase() ? -4 : 0;
    const titleCase = /\s/.test(value) && /[a-z]/.test(value) && /[A-Z]/.test(value) ? 3 : 0;
    const uppers = Math.min((value.match(/[A-Z]/g) || []).length, 6);
    return spaced + slug + allCaps + titleCase + uppers;
  };
  return score(next) > score(current) ? next : current;
}

/** Lowercase, punctuation-free, single-spaced — for word-boundary comparisons. */
function normalizeForPrefixMatch(value: string): string {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * Display name with the brand in front ("Aputure LS 600d"), skipping brands the
 * name already leads with. Keep in sync with `brandedProductName` in
 * `src/utils/brandIdentity.ts`.
 *
 * Display only — the stored `name` is never rewritten, because it feeds the
 * scrape fingerprint on the API side.
 */
export function brandedProductName(brand?: string, name?: string): string {
  const label = String(name || '').trim();
  const brandLabel = String(brand || '').trim();
  if (!brandLabel) return label;
  if (!label) return brandLabel;

  const nameKey = normalizeForPrefixMatch(label);
  const brandKey = normalizeForPrefixMatch(brandLabel);
  if (!brandKey) return label;
  if (nameKey === brandKey || nameKey.startsWith(`${brandKey} `)) return label;

  return `${brandLabel} ${label}`;
}

export function mergeBrandLabels(names: string[]): string[] {
  const byKey = new Map<string, string>();
  for (let i = 0; i < names.length; i += 1) {
    const raw = names[i];
    if (!raw || typeof raw !== 'string') continue;
    const name = raw.trim();
    if (!name) continue;
    const key = brandFamilyKey(name);
    if (!key) continue;
    const labeled = brandFamilyDisplayName(name) || name;
    const existing = byKey.get(key);
    byKey.set(
      key,
      existing
        ? brandFamilyDisplayName(existing) ||
            brandFamilyDisplayName(name) ||
            preferBrandDisplayName(existing, labeled)
        : labeled,
    );
  }
  const labels: string[] = [];
  byKey.forEach((name) => {
    labels.push(name);
  });
  return labels.sort((a, b) => a.localeCompare(b));
}

export function brandsMatchIdentity(a?: string, b?: string): boolean {
  const left = brandFamilyKey(a || '');
  const right = brandFamilyKey(b || '');
  return !!left && left === right;
}

export type BrandIdentityCandidate = string | { id?: string; slug?: string; name?: string };

/** Map a slug like "omsystem" onto the catalog label "OM System" when present. */
export function resolveBrandDisplayName(
  raw: string,
  candidates: BrandIdentityCandidate[]
): string {
  const input = String(raw || '').trim();
  if (!input) return '';
  const key = brandIdentityKey(input);
  if (!key) return input;

  let best = '';
  for (const candidate of candidates) {
    if (typeof candidate === 'string') {
      const name = candidate.trim();
      if (!name || !brandsMatchIdentity(name, input)) continue;
      best = best ? preferBrandDisplayName(best, brandFamilyDisplayName(name) || name) : brandFamilyDisplayName(name) || name;
      continue;
    }
    const labels = [candidate.name, candidate.slug, candidate.id]
      .map((value) => String(value || '').trim())
      .filter(Boolean);
    if (!labels.some((label) => brandsMatchIdentity(label, input))) continue;
    const name = String(candidate.name || candidate.slug || candidate.id || '').trim();
    if (!name) continue;
    best = best ? preferBrandDisplayName(best, name) : name;
  }
  return best || input;
}
